import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { Sucursal } from '../../utils/sucursalContext';

type OperationMode = 'RESERVATION' | 'SMART_OCCUPANCY' | 'HYBRID';

type GymConfig = {
  gimnasio_id: string;
  operation_mode: OperationMode;
  reservations_enabled: boolean;
  smart_occupancy_enabled: boolean;
  time_band_plans_enabled: boolean;
  flex_pass_enabled: boolean;
  dynamic_pricing_enabled: boolean;
  occupancy_recommendations_enabled: boolean;
  coach_acceso_minutos_antes: number;
  coach_acceso_minutos_despues: number;
  created_at?: string;
  updated_at?: string;
};

type BranchConfig = {
  sucursal_id: string;
  gimnasio_id: string;
  timezone: string;
  capacidad_operativa: number;
  umbral_bajo_pct: number;
  umbral_medio_pct: number;
  permitir_sobrecapacidad: boolean;
  tolerancia_dato_minutos: number;
  hora_reconciliacion: string;
  created_at?: string;
  updated_at?: string;
};

const defaultGym = (gimnasioId: string): GymConfig => ({
  gimnasio_id: gimnasioId,
  operation_mode: 'RESERVATION',
  reservations_enabled: true,
  smart_occupancy_enabled: false,
  time_band_plans_enabled: false,
  flex_pass_enabled: false,
  dynamic_pricing_enabled: false,
  occupancy_recommendations_enabled: false,
  coach_acceso_minutos_antes: 15,
  coach_acceso_minutos_despues: 15,
});

const defaultBranch = (branch: Sucursal, gimnasioId: string): BranchConfig => ({
  sucursal_id: branch.id,
  gimnasio_id: gimnasioId,
  timezone: 'America/Santiago',
  capacidad_operativa: Math.max(1, Number(branch.capacidad_maxima) || 150),
  umbral_bajo_pct: 40,
  umbral_medio_pct: 75,
  permitir_sobrecapacidad: false,
  tolerancia_dato_minutos: 10,
  hora_reconciliacion: '04:00',
});

const modePreset = (config: GymConfig, mode: OperationMode): GymConfig => ({
  ...config,
  operation_mode: mode,
  reservations_enabled: mode !== 'SMART_OCCUPANCY',
  smart_occupancy_enabled: mode !== 'RESERVATION',
  time_band_plans_enabled: mode !== 'RESERVATION',
  occupancy_recommendations_enabled: mode !== 'RESERVATION',
  flex_pass_enabled: mode === 'RESERVATION' ? false : config.flex_pass_enabled,
  dynamic_pricing_enabled: mode === 'RESERVATION' ? false : config.dynamic_pricing_enabled,
});

export default function OperationalModelSettings({
  gimnasioId,
  sucursales,
  onClose,
}: {
  gimnasioId: string | null;
  sucursales: Sucursal[];
  onClose: () => void;
}) {
  const scopedBranches = useMemo(
    () => sucursales.filter((branch) => !gimnasioId || branch.gimnasio_id === gimnasioId),
    [gimnasioId, sucursales],
  );
  const [gym, setGym] = useState<GymConfig | null>(null);
  const [branches, setBranches] = useState<BranchConfig[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedBranch = useMemo(
    () => branches.find((branch) => branch.sucursal_id === selectedBranchId) || null,
    [branches, selectedBranchId],
  );

  const load = useCallback(async () => {
    if (!gimnasioId) {
      setError('No se pudo determinar el gimnasio activo.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const [gymResult, branchResult] = await Promise.all([
      supabase.from('gimnasio_operacion_config').select('*').eq('gimnasio_id', gimnasioId).maybeSingle(),
      supabase.from('sucursal_ocupacion_config').select('*').eq('gimnasio_id', gimnasioId),
    ]);

    if (gymResult.error || branchResult.error) {
      const message = gymResult.error?.message || branchResult.error?.message || '';
      setError(message.includes('operacion_config') || message.includes('ocupacion_config')
        ? 'Falta aplicar la migración Smart Occupancy en Supabase.'
        : message);
      setLoading(false);
      return;
    }

    setGym((gymResult.data as GymConfig | null) || defaultGym(gimnasioId));
    const stored = (branchResult.data || []) as BranchConfig[];
    const merged = scopedBranches.map((branch) =>
      stored.find((item) => item.sucursal_id === branch.id) || defaultBranch(branch, gimnasioId));
    setBranches(merged);
    setSelectedBranchId((current) => merged.some((item) => item.sucursal_id === current)
      ? current : (merged[0]?.sucursal_id || ''));
    setLoading(false);
  }, [gimnasioId, scopedBranches]);

  useEffect(() => { void load(); }, [load]);

  const updateBranch = (patch: Partial<BranchConfig>) => {
    setBranches((current) => current.map((branch) =>
      branch.sucursal_id === selectedBranchId ? { ...branch, ...patch } : branch));
  };

  const save = async () => {
    if (!gym || !gimnasioId) return;
    if (branches.some((branch) => branch.umbral_bajo_pct >= branch.umbral_medio_pct)) {
      setError('El umbral bajo debe ser menor que el umbral medio.');
      return;
    }
    if (gym.coach_acceso_minutos_antes < 0 || gym.coach_acceso_minutos_antes > 180 || gym.coach_acceso_minutos_despues < 0 || gym.coach_acceso_minutos_despues > 180) {
      setError('La ventana de acceso para coaches debe estar entre 0 y 180 minutos.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    const normalizedGym = {
      ...gym,
      dynamic_pricing_enabled: gym.flex_pass_enabled && gym.dynamic_pricing_enabled,
      created_at: gym.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const now = new Date().toISOString();
    const normalizedBranches = branches.map((branch) => ({
      ...branch,
      created_at: branch.created_at || now,
      updated_at: now,
    }));
    const [gymResult, branchesResult] = await Promise.all([
      supabase.from('gimnasio_operacion_config').upsert(normalizedGym, { onConflict: 'gimnasio_id' }),
      supabase.from('sucursal_ocupacion_config').upsert(normalizedBranches, { onConflict: 'sucursal_id' }),
    ]);
    const saveError = gymResult.error || branchesResult.error;
    if (saveError) setError(saveError.message);
    else {
      setGym(normalizedGym);
      setSuccess('Configuración operativa guardada. La app móvil utilizará estos valores.');
    }
    setSaving(false);
  };

  if (loading) return <div className="py-16 text-center text-gray-500"><i className="ri-loader-4-line animate-spin text-2xl"/><p className="mt-2 text-sm">Cargando modelo operativo…</p></div>;

  return (
    <div className="text-left">
      <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Modelo operativo</h3>
          <p className="text-sm text-gray-500 mt-1">Reservas, ocupación inteligente y capacidad por sucursal</p>
        </div>
        <button type="button" onClick={onClose} className="p-2 -mr-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg" aria-label="Cerrar">
          <i className="ri-close-line text-xl"/>
        </button>
      </div>
      <div className="px-6 py-5 max-h-[70vh] overflow-y-auto space-y-5">
        {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
        {success && <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">{success}</div>}
        {gym && <>
          <section>
            <p className="text-sm font-semibold text-gray-900 mb-3">Funcionamiento del gimnasio</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {([
                ['RESERVATION', 'Reservas', 'Mantiene clases y reservas actuales.'],
                ['SMART_OCCUPANCY', 'Ocupación inteligente', 'Acceso por franja, aforo y recomendaciones.'],
                ['HYBRID', 'Híbrido', 'Combina reservas y ocupación inteligente.'],
              ] as const).map(([mode, title, description]) => (
                <button key={mode} type="button" onClick={() => setGym(modePreset(gym, mode))}
                  className={`p-4 rounded-xl border text-left transition-colors ${gym.operation_mode === mode ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <div className="flex items-center justify-between"><p className="text-sm font-semibold text-gray-900">{title}</p>{gym.operation_mode === mode && <i className="ri-checkbox-circle-fill text-teal-600"/>}</div>
                  <p className="text-xs text-gray-500 mt-1">{description}</p>
                </button>
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Toggle title="Planes por franja" description="Valida las horas permitidas por el plan." checked={gym.time_band_plans_enabled} disabled={!gym.smart_occupancy_enabled} onChange={(value) => setGym({ ...gym, time_band_plans_enabled: value })}/>
            <Toggle title="Recomendaciones de ocupación" description="Muestra horarios convenientes en la app." checked={gym.occupancy_recommendations_enabled} disabled={!gym.smart_occupancy_enabled} onChange={(value) => setGym({ ...gym, occupancy_recommendations_enabled: value })}/>
            <Toggle title="Flex Pass" description="Habilita accesos adicionales según disponibilidad." checked={gym.flex_pass_enabled} disabled={!gym.smart_occupancy_enabled} onChange={(value) => setGym({ ...gym, flex_pass_enabled: value, dynamic_pricing_enabled: value ? gym.dynamic_pricing_enabled : false })}/>
            <Toggle title="Precio dinámico" description="Ajusta Flex Pass según demanda (fase posterior)." checked={gym.dynamic_pricing_enabled} disabled={!gym.flex_pass_enabled} onChange={(value) => setGym({ ...gym, dynamic_pricing_enabled: value })}/>
          </section>

          <section className="border border-gray-200 rounded-xl p-4">
            <div className="mb-4"><p className="text-sm font-semibold text-gray-900">Acceso de clientes de coaches externos</p><p className="text-xs text-gray-500 mt-1">Ventana durante la cual una reserva confirmada permite ingresar por el torniquete.</p></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Permitir entrada antes del bloque (minutos)"><input type="number" min="0" max="180" value={gym.coach_acceso_minutos_antes} onChange={(event)=>setGym({...gym,coach_acceso_minutos_antes:Number(event.target.value)})} className="input"/></Field>
              <Field label="Permitir entrada después de iniciado/finalizado (minutos)"><input type="number" min="0" max="180" value={gym.coach_acceso_minutos_despues} onChange={(event)=>setGym({...gym,coach_acceso_minutos_despues:Number(event.target.value)})} className="input"/></Field>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-3 mt-4">Ejemplo: para un bloque de 18:00 a 19:00 con 15 minutos, el acceso estará habilitado entre 17:45 y 19:15.</p>
          </section>

          <section className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div><p className="text-sm font-semibold text-gray-900">Ocupación por sucursal</p><p className="text-xs text-gray-500">Capacidad, umbrales y frescura del contador.</p></div>
              <select value={selectedBranchId} onChange={(event) => setSelectedBranchId(event.target.value)} className="input md:w-64">
                {scopedBranches.map((branch) => <option key={branch.id} value={branch.id}>{branch.nombre}</option>)}
              </select>
            </div>
            {selectedBranch && <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Zona horaria"><input value={selectedBranch.timezone} onChange={(event) => updateBranch({ timezone: event.target.value })} className="input" placeholder="America/Santiago"/></Field>
              <Field label="Capacidad operativa"><input type="number" min="1" max="100000" value={selectedBranch.capacidad_operativa} onChange={(event) => updateBranch({ capacidad_operativa: Number(event.target.value) })} className="input"/></Field>
              <Field label="Ocupación baja hasta (%)"><input type="number" min="0" max="99" step="1" value={selectedBranch.umbral_bajo_pct} onChange={(event) => updateBranch({ umbral_bajo_pct: Number(event.target.value) })} className="input"/></Field>
              <Field label="Ocupación media hasta (%)"><input type="number" min="1" max="100" step="1" value={selectedBranch.umbral_medio_pct} onChange={(event) => updateBranch({ umbral_medio_pct: Number(event.target.value) })} className="input"/></Field>
              <Field label="Dato vigente durante (minutos)"><input type="number" min="1" max="1440" value={selectedBranch.tolerancia_dato_minutos} onChange={(event) => updateBranch({ tolerancia_dato_minutos: Number(event.target.value) })} className="input"/></Field>
              <Field label="Reconciliación diaria"><input type="time" value={selectedBranch.hora_reconciliacion.slice(0, 5)} onChange={(event) => updateBranch({ hora_reconciliacion: event.target.value })} className="input"/></Field>
              <div className="md:col-span-2"><Toggle title="Permitir sobrecapacidad" description="Autoriza ingresos cuando se supera la capacidad operativa." checked={selectedBranch.permitir_sobrecapacidad} onChange={(value) => updateBranch({ permitir_sobrecapacidad: value })}/></div>
            </div>}
          </section>
        </>}
      </div>
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm">Cerrar</button>
        <button type="button" onClick={() => void load()} className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm">Descartar cambios</button>
        <button type="button" disabled={saving || !gym} onClick={() => void save()} className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm disabled:opacity-60">{saving ? 'Guardando…' : 'Guardar configuración'}</button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="block text-xs font-medium text-gray-600 mb-1.5">{label}</span>{children}</label>;
}

function Toggle({ title, description, checked, disabled = false, onChange }: { title: string; description: string; checked: boolean; disabled?: boolean; onChange: (value: boolean) => void }) {
  return <label className={`flex items-center justify-between gap-4 p-4 border border-gray-200 rounded-lg ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}><div><p className="text-sm font-medium text-gray-900">{title}</p><p className="text-xs text-gray-500 mt-1">{description}</p></div><input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} className="w-4 h-4 text-teal-600 rounded"/></label>;
}
